/* eslint-disable no-underscore-dangle */
import {
  formatApiAddress,
  formatBoolean,
  isFieldsAvailableInLanguage,
  translatedFieldWithFallback,
  useRouter,
  useSlideshow,
} from '@application/utils';
import { CenterTypeEnum, ProfessionalTypeEnum } from '@gql/graphql';
import type { ProGetProQuery } from '@gql/graphql';
import type { DetailListContactData, SlideshowItemData } from '@presentation/components';
import type { ProItemData } from '@presentation/pages';
import { useMemo } from 'react';

type Pro = NonNullable<ProGetProQuery['searchProfessionals']>['professionals'][number];

export const useFormatPro = (pro: Pro | undefined, type?: ProfessionalTypeEnum) => {
  const { translate, language } = useRouter();

  const slideshowItems = useMemo((): SlideshowItemData[] => {
    const items: SlideshowItemData[] = (pro?.imgs ?? []).map((img) => ({
      src: (img.medium ?? img.large) as string,
    }));
    const video = pro?.profileVideo;
    if (video) {
      const videoIndex = pro?.profileVideoPos;
      const clampedIndex = Math.min(Math.max(videoIndex ?? 0, 0), items.length);
      items.splice(clampedIndex, 0, {
        src: video,
        isVideo: true,
      });
    }
    return items;
  }, [pro?.imgs, pro?.profileVideo, pro?.profileVideoPos]);
  const slideshow = useSlideshow(slideshowItems);

  const formattedPro: ProItemData | undefined = useMemo(() => {
    if (!pro) return;
    const contactInfo: DetailListContactData = {
      phoneNumber: pro?.phone ?? undefined,
      mobilePhoneNumber: pro?.mobilePhone ?? undefined,
      email: pro?.unclaimedProfile ? undefined : pro?.email ?? undefined,
      website: {
        label: formatBoolean(translate, !!pro?.website),
        url: pro?.website ?? undefined,
      },
      address: {
        label: formatApiAddress({ ...pro?.address }),
      },
    };

    return {
      proCenterType: pro.centerType ?? CenterTypeEnum.CENTRE,
      proType: type ?? pro.type ?? ProfessionalTypeEnum.VET,
      addressCoordinates: {
        lat: pro.address?.lat as number,
        lng: pro.address?.lng as number,
      },
      city: pro.address?.city || undefined,
      contactInfo,
      description: translatedFieldWithFallback(language, pro.translations, 'description'),
      wellnessEngagement: translatedFieldWithFallback(
        language,
        pro.translations,
        'wellnessEngagement',
      ),
      extraDescription: translatedFieldWithFallback(language, pro.translations, 'extraDescription'),
      onlineBookingEnabled: pro.onlineBookingEnabled ?? false,
    } as ProItemData;
  }, [language, pro, translate, type]);

  const imagesToPreload = useMemo(() => {
    const firstSlideshowItem = formattedPro?.slideshow?.items[0];
    if (firstSlideshowItem && !firstSlideshowItem.isVideo) {
      return [firstSlideshowItem.src];
    }
    return [];
  }, [formattedPro?.slideshow?.items]);

  const proContentLanguage = useMemo(() => {
    const currentLangAvailable = isFieldsAvailableInLanguage(language, pro?.translations, [
      'description',
    ]);
    if (currentLangAvailable) return language;
    return pro?.language;
  }, [language, pro?.language, pro?.translations]);

  return {
    formattedPro,
    slideshow,
    imagesToPreload,
    proContentLanguage,
  };
};
